import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { getDashboardStats } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopUpPackages } from "@/components/TopUpPackages";
import { creditsToTime } from "@/components/PricingSection";
import { Play, Coins, Flame, Radio } from "lucide-react";
import { logWarn } from "@/lib/log";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="border border-white/10 bg-surface p-6" data-testid={testid}>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
            <Icon className="w-3.5 h-3.5 text-blaze" />
            {label}
        </div>
        <div className="font-display font-bold text-4xl">{value}</div>
        {sub && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">{sub}</div>}
    </div>
);

const DashboardHeader = ({ displayName }) => (
    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blaze">/ Dashboard</div>
            <h1 className="font-display font-bold uppercase text-3xl mt-2 tracking-tight">
                Welcome, {displayName}
            </h1>
        </div>
        <Link
            to="/console/stream"
            className="bg-blaze hover:bg-blazeHover text-white font-mono uppercase text-[12px] tracking-[0.25em] px-8 py-4 flex items-center gap-3 transition-colors"
            data-testid="dashboard-start-stream-button"
        >
            <Play className="w-4 h-4 fill-current" />
            Start Stream
        </Link>
    </div>
);

const StatsRow = ({ credits, stats }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <StatCard
            icon={Coins}
            label="Credits Left"
            value={credits.toLocaleString()}
            sub={`≈ ${creditsToTime(credits)} of streaming`}
            testid="stat-credits-left"
        />
        <StatCard
            icon={Flame}
            label="Credits Used"
            value={(stats?.credits_used ?? 0).toLocaleString()}
            sub="All time"
            testid="stat-credits-used"
        />
        <StatCard
            icon={Radio}
            label="Total Streams"
            value={(stats?.total_streams ?? 0).toLocaleString()}
            sub="Studio + OBS sessions"
            testid="stat-total-streams"
        />
    </div>
);

const SessionRow = ({ session }) => (
    <div className="py-3 flex items-center justify-between font-mono text-xs">
        <div className="text-white/70">
            {session.source === "obs" ? "OBS" : "Studio"} stream
            {session.active && <span className="text-blaze ml-2">● LIVE</span>}
        </div>
        <div className="text-white/40">{session.seconds}s · −{session.credits_spent} credits</div>
        <div className="text-white/30 hidden sm:block">
            {new Date(session.started_at).toLocaleString()}
        </div>
    </div>
);

const RecentActivity = ({ sessions }) => (
    <div className="border border-white/10 bg-surface p-6" data-testid="recent-activity">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-4">
            Recent Activity
        </div>
        {sessions?.length ? (
            <div className="divide-y divide-white/5">
                {sessions.map((s) => (
                    <SessionRow key={s.stream_id} session={s} />
                ))}
            </div>
        ) : (
            <div className="font-mono text-xs text-white/30 py-4">
                No streams yet — hit Start Stream to go live.
            </div>
        )}
    </div>
);

export default function Dashboard() {
    const { user, updateCredits } = useAuth();
    const [stats, setStats] = useState(null);

    const load = useCallback(async () => {
        try {
            const s = await getDashboardStats();
            setStats(s);
            updateCredits(s.credits);
        } catch (e) {
            logWarn("stats load failed", e);
        }
    }, [updateCredits]);

    useEffect(() => {
        load();
    }, [load]);

    const credits = stats?.credits ?? user?.credits ?? 0;

    return (
        <div className="max-w-6xl" data-testid="dashboard-page">
            <DashboardHeader displayName={user?.display_name} />
            <StatsRow credits={credits} stats={stats} />
            <div className="border border-white/10 bg-surface p-6 mb-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-6">
                    Top Up Credits
                </div>
                <TopUpPackages onToppedUp={() => load()} />
            </div>
            <RecentActivity sessions={stats?.recent_sessions} />
        </div>
    );
}
